import isEqual from 'lodash/isEqual';
import { useEffect, useRef } from 'react';

import { initialDrawerState } from '../../../reducers';
import { isWithinDrawerHighlightZoomBounds } from '../../../consts';
import {
    isBitmapInfo,
    isCaptureFeature,
    isClusteredPointOGITileFeature,
    isGeoJsonFeature,
    HighlightedObject
} from '../../../types';

/**
 *
 */
export function useShouldCloseDrawers({
    drawerState,
    highlightedFeature,
    highlightedLayerId,
    zoom,
    globalDate,
    platform,
    product,
    closeDrawer,
    keepOpenWithoutHighlight = false
}: {
    drawerState: typeof initialDrawerState;
    highlightedFeature: HighlightedObject;
    highlightedLayerId: string | null;
    zoom: number;
    globalDate: number;
    platform: string;
    product: string;
    closeDrawer: () => void;
    keepOpenWithoutHighlight?: boolean;
}) {
    const prevHighlight = useRef<HighlightedObject>(highlightedFeature);
    const prevPlatform = useRef<string>(platform);
    const prevProduct = useRef<string>(product);

    useEffect(() => {
        let sameId = false;
        let sameProperties = false;
        if (
            (isGeoJsonFeature(highlightedFeature) && isGeoJsonFeature(prevHighlight.current)) ||
            (isCaptureFeature(highlightedFeature) && isCaptureFeature(prevHighlight.current))
        ) {
            // both are geojson or both are captures
            sameId = highlightedFeature.id === prevHighlight.current?.id;

            if (
                isClusteredPointOGITileFeature(highlightedFeature) ||
                isClusteredPointOGITileFeature(prevHighlight.current)
            ) {
                // must check clustered OGI points differently because their ids
                // are assigned semi-randomly by tippecanoe at the time of tile-building
                sameProperties = isEqual(highlightedFeature.properties, prevHighlight.current?.properties);
            } else {
                // assume everything else with the same id is the same actual feature
                sameProperties = sameId;
            }
        } else if (isBitmapInfo(highlightedFeature) && isBitmapInfo(prevHighlight.current)) {
            // both are bitmap
            sameId = highlightedFeature.pixel === prevHighlight.current.pixel;
            sameProperties = isEqual(highlightedFeature, prevHighlight.current);
        }

        const highlightIsNothing = highlightedFeature === undefined || highlightedFeature === null;
        const highlightWasNothing = prevHighlight.current === undefined || prevHighlight.current === null;

        // plumes are geojson features but do not have ids. we should leave
        const noHighlightedFeatureId = highlightedFeature && (!('id' in highlightedFeature) || !highlightedFeature.id);

        const changedProductOrPlatform = platform !== prevPlatform.current || product !== prevProduct.current;
        let shouldLeaveDataDrawerOpen = false;

        // leave drawer open if platform is NOT changed and there is a highlighted feature and...
        if (!changedProductOrPlatform && highlightedFeature) {
            // ...AND one of the following
            if (highlightWasNothing) {
                // there was no highlight previously
                shouldLeaveDataDrawerOpen = true;
            } else if (sameId && sameProperties) {
                // the highlight is unchanged
                shouldLeaveDataDrawerOpen = true;
            } else if (!sameId) {
                // the highlight has changed - user clicked a new feature
                shouldLeaveDataDrawerOpen = true;
            } else if (noHighlightedFeatureId && !sameProperties) {
                /** highlight does not have an id and properties are new -
                 * This allows leaving the drawer open when a feature without an id - a plume - is
                 * clicked for the first time. In that case, no other conditions would be met.
                 */
                shouldLeaveDataDrawerOpen = true;
            }
        }
        const dataDrawerOpen = drawerState.emissionsDetails || drawerState.ogiData || drawerState.targetData;
        // Do not close the drawer just because the highlight is missing when the page wants to keep it open (e.g. to display "data unavailable" cards)
        const suppressCloseWithoutHighlight = keepOpenWithoutHighlight && highlightIsNothing;
        if (
            !suppressCloseWithoutHighlight &&
            (highlightIsNothing ||
                !shouldLeaveDataDrawerOpen ||
                !isWithinDrawerHighlightZoomBounds(zoom, highlightedLayerId, highlightedFeature)) &&
            dataDrawerOpen
        ) {
            closeDrawer();
        }
        prevHighlight.current = highlightedFeature;
        prevPlatform.current = platform;
        prevProduct.current = product;
    }, [
        drawerState.noData,
        drawerState.emissionsDetails,
        drawerState.ogiData,
        drawerState.targetData,
        highlightedFeature,
        highlightedLayerId,
        zoom,
        globalDate,
        platform,
        product,
        keepOpenWithoutHighlight,
        closeDrawer
    ]);
}
